/**
 * "Everything open", as a board of priority tiles.
 *
 * The same tiles the category boards use, grouped by priority instead of by
 * subcategory. Items here come from everywhere, so each one shows its
 * breadcrumb -- that is the only thing saying where it belongs.
 *
 * A priority with nothing in it is not rendered. An empty tile carries no
 * information the header does not already give, and four of them make the
 * section look full when it is not.
 */

import { useState } from 'react'

import type { Item, ItemPriority } from '@/api/types'
import { PRIORITIES } from '@/api/types'
import { BoardItem, BoardTile, TileOverdue } from '@/components/BoardTile'
import { readableTextOn, subcategoryShade } from '@/lib/color'
import { PRIORITY_LABEL, formatDue } from '@/lib/format'

/** The same hues the priority pills and bars use, so the tile reads as the
 *  same thing in a different form. */
const PRIORITY_COLOR: Record<ItemPriority, string> = {
  imminent: '#a90533',
  high: '#f59e0b',
  medium: '#3b82f6',
  low: '#6b7280',
}

/** Tinted through the same function as the subcategory tiles, so both boards
 *  land on the same depth of colour rather than two hand-picked sets. */
function tintFor(priority: ItemPriority) {
  const background = subcategoryShade(PRIORITY_COLOR[priority])
  return { background, text: readableTextOn(background) }
}

interface PriorityBoardProps {
  items: Item[]
  today: string
  /** Which priorities the user has chosen to see here. */
  visible: Record<ItemPriority, boolean>
  onComplete: (item: Item) => void
  onItemStatus: (item: Item, next: string | null) => void | Promise<void>
  onPriorityChange: (item: Item, priority: ItemPriority) => void
  onAddTask: (priority: ItemPriority) => void
  /** The dashboard item limit, applied per tile. One priority with eighty
   *  items should not push the other three off the screen. */
  limit: number
}

export function PriorityBoard({
  items,
  today,
  visible,
  onComplete,
  onItemStatus,
  onPriorityChange,
  onAddTask,
  limit,
}: PriorityBoardProps) {
  const [expanded, setExpanded] = useState<Partial<Record<ItemPriority, boolean>>>({})
  const groups = PRIORITIES.filter((priority) => visible[priority])
    .map((priority) => ({
      priority,
      items: items.filter((item) => item.priority === priority),
    }))
    // Hide a priority with nothing in it, as asked.
    .filter((group) => group.items.length > 0)

  if (groups.length === 0) return null

  // Two across at most, so four priorities read as a 2x2 block. Narrower
  // columns wrapped every title several lines deep; the tiles are worth more
  // width than the row is worth density. Written as whole class strings
  // because Tailwind scans source text literally.
  const columns = groups.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'

  return (
    <div className={`grid items-start gap-4 ${columns}`}>
      {groups.map(({ priority, items: groupItems }) => {
        const tint = tintFor(priority)
        const overdue = groupItems.filter(
          (item) => formatDue(item.due_date, today)?.overdue,
        ).length

        return (
          <BoardTile
            key={priority}
            title={PRIORITY_LABEL[priority]}
            titleId={`priority-${priority}`}
            count={groupItems.length}
            tint={tint}
            onAddTask={() => onAddTask(priority)}
            addLabel={`Add a ${PRIORITY_LABEL[priority].toLowerCase()} priority task`}
          >
            <TileOverdue count={overdue} />
            <ul className="m-0 list-none p-0">
              {(expanded[priority] ? groupItems : groupItems.slice(0, limit)).map((item) => (
                <BoardItem
                  key={item.id}
                  item={item}
                  today={today}
                  onComplete={onComplete}
                  onItemStatus={onItemStatus}
                  onPriorityChange={onPriorityChange}
                  // These items come from every category, so each needs to say
                  // where it lives.
                  showBreadcrumb
                />
              ))}
            </ul>

            {groupItems.length > limit && (
              <div className="border-t border-line px-3 py-2 text-center">
                <button
                  type="button"
                  className="btn btn-neutral btn-sm"
                  onClick={() =>
                    setExpanded((current) => ({ ...current, [priority]: !current[priority] }))
                  }
                >
                  {expanded[priority] ? 'Show fewer' : `Show all ${groupItems.length}`}
                </button>
              </div>
            )}
          </BoardTile>
        )
      })}
    </div>
  )
}
